'use client'

import { useState, useEffect, useRef } from 'react'
import { useRouter } from 'next/navigation'
import { useApp } from '@/context/AppContext'
import { useLang } from '@/context/LangContext'
import { api } from '@/lib/api'
import { ArrowLeft, CheckCircle } from 'lucide-react'
import Link from 'next/link'
import { BarcodeScanner } from '@/components/BarcodeScanner'

type Step = 'barcode' | 'info' | 'prix' | 'stock' | 'done'

export default function NewArticlePage() {
  const { session } = useApp()
  const { t } = useLang()
  const router = useRouter()

  const [step, setStep] = useState<Step>('barcode')
  const [loading, setLoading] = useState(false)
  const [showScanner, setShowScanner] = useState(false)
  const [checking, setChecking] = useState(false)
  const [suggestions, setSuggestions] = useState<any[]>([])
  const [searching, setSearching] = useState(false)
  const searchTimeout = useRef<ReturnType<typeof setTimeout> | null>(null)

  const [form, setForm] = useState({
    name: '', barcode: '', category_id: '', unit: 'pièce',
    ras_mel: '', prix_vente: '', prix_min: '', stock: '', stock_alert: '5',
  })
  const [errors, setErrors] = useState<Record<string, string>>({})

  useEffect(() => {
    if (!session) router.replace('/login')
  }, [session])

  function set(key: string, val: string) {
    setForm(f => ({ ...f, [key]: val }))
    setErrors(e => ({ ...e, [key]: '' }))

    if (key === 'barcode') {
      const v = val.toUpperCase()
      if (searchTimeout.current) clearTimeout(searchTimeout.current)
      if (v.length >= 3) {
        setSearching(true)
        searchTimeout.current = setTimeout(async () => {
          const res = await fetch(`/api/articles?shop_id=${session!.shop.id}&search=${encodeURIComponent(v)}`)
          const data = await res.json()
          setSuggestions(Array.isArray(data) ? data.slice(0, 6) : [])
          setSearching(false)
        }, 300)
      } else {
        setSuggestions([])
        setSearching(false)
      }
    }
  }

  async function checkBarcode(code: string) {
    const trimmed = code.trim().toUpperCase()
    if (!trimmed) { setErrors({ barcode: 'Référence obligatoire' }); return }
    setChecking(true)
    setSuggestions([])
    const res = await fetch(`/api/articles?shop_id=${session!.shop.id}&search=${encodeURIComponent(trimmed)}`)
    const articles = await res.json()
    setChecking(false)
    const existing = Array.isArray(articles)
      ? articles.find((a: any) => a.barcode?.toUpperCase() === trimmed)
      : null
    if (existing) {
      router.push(`/articles/${existing.id}`)
    } else {
      setForm(f => ({ ...f, barcode: trimmed }))
      setStep('info')
    }
  }

  function validate() {
    const e: Record<string, string> = {}
    if (step === 'info' && !form.name.trim()) e.name = 'Nom obligatoire'
    if (step === 'prix') {
      if (!form.ras_mel) e.ras_mel = 'Obligatoire'
      if (!form.prix_vente) e.prix_vente = 'Obligatoire'
      if (!form.prix_min) e.prix_min = 'Obligatoire'
      if (form.prix_min && form.prix_vente && parseFloat(form.prix_min) > parseFloat(form.prix_vente))
        e.prix_min = 'Prix min > prix vente!'
    }
    if (step === 'stock' && !form.stock) e.stock = 'Obligatoire'
    setErrors(e)
    return Object.keys(e).length === 0
  }

  async function next() {
    if (!validate()) return
    const steps: Step[] = ['info', 'prix', 'stock', 'done']
    const idx = steps.indexOf(step)
    if (steps[idx + 1] === 'done') {
      setLoading(true)
      await api.createArticle({
        shop_id: session!.shop.id,
        name: form.name.trim(),
        barcode: form.barcode || null,
        category_id: form.category_id || null,
        unit: form.unit,
        ras_mel: parseFloat(form.ras_mel),
        prix_vente: parseFloat(form.prix_vente),
        prix_min: parseFloat(form.prix_min),
        stock: parseInt(form.stock),
        stock_alert: parseInt(form.stock_alert),
      })
      setLoading(false)
    }
    setStep(steps[idx + 1])
  }

  const stepNum: Record<Step, number> = { barcode: 0, info: 1, prix: 2, stock: 3, done: 4 }
  const stepLabels = [
    { s: 'info', label: '1. Article' },
    { s: 'prix', label: '2. Prix' },
    { s: 'stock', label: '3. Stock' },
  ]

  return (
    <div className="min-h-screen pb-6">
      {showScanner && (
        <BarcodeScanner
          title="Scanner le code-barres"
          onDetected={code => { setShowScanner(false); checkBarcode(code) }}
          onClose={() => setShowScanner(false)}
        />
      )}

      {/* Header */}
      <div className="bg-white sticky top-0 z-10 shadow-sm">
        <div className="flex items-center gap-3 px-4 pt-12 pb-4">
          <Link href="/articles" className="p-2 -ml-2 text-gray-600">
            <ArrowLeft className="w-6 h-6" />
          </Link>
          <h1 className="text-xl font-bold text-gray-900">Nouvel article</h1>
        </div>
        {step !== 'barcode' && step !== 'done' && (
          <div className="flex px-4 pb-4 gap-2">
            {stepLabels.map(({ s, label }, i) => (
              <div key={s} className="flex-1 flex flex-col items-center gap-1">
                <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold
                  ${s === step ? 'bg-indigo-600 text-white'
                    : stepNum[s as Step] < stepNum[step] ? 'bg-green-500 text-white'
                    : 'bg-gray-100 text-gray-400'}`}>
                  {i + 1}
                </div>
                <span className="text-xs text-gray-500">{label}</span>
              </div>
            ))}
          </div>
        )}
      </div>

      <div className="px-4 pt-6">

        {/* ── STEP 0: BARCODE / REF ── */}
        {step === 'barcode' && (
          <div className="space-y-4">
            {/* Scan barcode */}
            <button
              onClick={() => setShowScanner(true)}
              className="w-full py-10 rounded-3xl border-2 border-dashed border-red-200 bg-red-50 flex flex-col items-center gap-3 active:scale-95 transition-transform"
            >
              <div className="w-16 h-16 bg-red-500 rounded-2xl flex items-center justify-center text-3xl">
                📷
              </div>
              <span className="text-red-700 font-bold text-lg">Scanner le code-barres</span>
              <span className="text-red-300 text-sm">Pointe la caméra vers le code</span>
            </button>

            {/* Divider */}
            <div className="flex items-center gap-3">
              <div className="flex-1 h-px bg-gray-200" />
              <span className="text-gray-400 text-sm font-medium">ou</span>
              <div className="flex-1 h-px bg-gray-200" />
            </div>

            {/* Manual ref */}
            <div>
              <label className="text-sm font-semibold text-gray-700 mb-1.5 block">
                Saisir la référence
              </label>
              <div className="relative">
                <input
                  value={form.barcode}
                  onChange={e => set('barcode', e.target.value.toUpperCase())}
                  onKeyDown={e => e.key === 'Enter' && checkBarcode(form.barcode)}
                  placeholder="Ex: CRDN570ZX ou 619123456789"
                  autoCapitalize="characters"
                  className={`w-full px-4 py-4 rounded-2xl border bg-gray-50 outline-none focus:bg-white focus:border-indigo-500 text-lg font-mono uppercase ${errors.barcode ? 'border-red-400' : 'border-gray-200'}`}
                />
                {searching && (
                  <span className="absolute right-4 top-1/2 -translate-y-1/2 w-5 h-5 border-2 border-indigo-400 border-t-transparent rounded-full animate-spin" />
                )}
              </div>
              {errors.barcode && <p className="text-red-500 text-sm mt-1">{errors.barcode}</p>}

              {/* Live suggestions */}
              {suggestions.length > 0 && (
                <div className="mt-2 border border-gray-200 rounded-2xl overflow-hidden shadow-lg bg-white">
                  {suggestions.map((a, i) => (
                    <button
                      key={a.id}
                      onClick={() => router.push(`/articles/${a.id}`)}
                      className={`w-full px-4 py-3 flex items-center justify-between text-left active:bg-indigo-50 ${i < suggestions.length - 1 ? 'border-b border-gray-100' : ''}`}
                    >
                      <div>
                        <p className="font-semibold text-gray-800 text-sm">{a.name}</p>
                        <p className="text-xs font-mono text-indigo-500">{a.barcode}</p>
                      </div>
                      <span className="text-xs text-gray-400 bg-gray-100 px-2 py-1 rounded-lg">
                        {a.stock} {a.unit}
                      </span>
                    </button>
                  ))}
                </div>
              )}
            </div>

            <button
              onClick={() => checkBarcode(form.barcode)}
              disabled={checking || !form.barcode.trim()}
              className="w-full py-4 bg-indigo-600 text-white rounded-2xl font-bold text-lg disabled:opacity-50 flex items-center justify-center gap-2 active:scale-95"
            >
              {checking
                ? <span className="w-6 h-6 border-2 border-white border-t-transparent rounded-full animate-spin" />
                : 'Vérifier →'}
            </button>
          </div>
        )}

        {/* ── STEP 1: INFO ── */}
        {step === 'info' && (
          <div className="space-y-4">
            <div className="flex items-center gap-2 bg-green-50 border border-green-200 rounded-2xl px-4 py-2.5">
              <CheckCircle className="w-4 h-4 text-green-500 shrink-0" />
              <span className="text-green-700 text-sm font-mono font-semibold flex-1">{form.barcode}</span>
              <button onClick={() => setStep('barcode')} className="text-gray-400 text-xs underline">Changer</button>
            </div>
            <div>
              <label className="text-sm font-semibold text-gray-700 mb-1.5 block">Nom *</label>
              <input
                autoFocus value={form.name}
                onChange={e => set('name', e.target.value)}
                placeholder="Ex: Réfrigérateur Condor 570L"
                className={`w-full px-4 py-3.5 rounded-2xl border bg-gray-50 outline-none focus:bg-white focus:border-indigo-500 ${errors.name ? 'border-red-400' : 'border-gray-200'}`}
              />
              {errors.name && <p className="text-red-500 text-sm mt-1">{errors.name}</p>}
            </div>
            <div>
              <label className="text-sm font-semibold text-gray-700 mb-1.5 block">Unité</label>
              <div className="flex gap-2">
                {['pièce', 'kg', 'litre', 'boîte'].map(u => (
                  <button key={u} onClick={() => set('unit', u)}
                    className={`flex-1 py-2.5 rounded-2xl text-sm font-semibold border-2 ${form.unit === u ? 'border-indigo-500 bg-indigo-50 text-indigo-700' : 'border-gray-200 text-gray-600'}`}>
                    {u}
                  </button>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* ── STEP 2: PRIX ── */}
        {step === 'prix' && (
          <div className="space-y-4">
            <div className="bg-amber-50 border border-amber-200 rounded-2xl p-3">
              <p className="text-amber-700 text-sm">
                💡 <strong>Ras mel</strong> = combien 5sertou. <strong>Prix min</strong> = moins que ça = alert!
              </p>
            </div>
            {[
              { key: 'ras_mel', label: 'Ras mel (Prix achat) *' },
              { key: 'prix_vente', label: 'Prix vente normal *' },
              { key: 'prix_min', label: 'Prix minimum (alerte) *' },
            ].map(({ key, label }) => (
              <div key={key}>
                <label className="text-sm font-semibold text-gray-700 mb-1.5 block">{label}</label>
                <div className="relative">
                  <input
                    type="number" inputMode="decimal"
                    value={(form as any)[key]}
                    onChange={e => set(key, e.target.value)}
                    placeholder="0.000"
                    className={`w-full px-4 py-3.5 pr-12 rounded-2xl border bg-gray-50 outline-none focus:bg-white focus:border-indigo-500 ${(errors as any)[key] ? 'border-red-400' : 'border-gray-200'}`}
                  />
                  <span className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-400">DT</span>
                </div>
                {(errors as any)[key] && <p className="text-red-500 text-sm mt-1">{(errors as any)[key]}</p>}
              </div>
            ))}
            {form.ras_mel && form.prix_vente && (
              <div className="bg-green-50 border border-green-200 rounded-2xl p-3">
                <p className="text-green-700 text-sm">
                  ✅ Marge: <strong>+{(((parseFloat(form.prix_vente) - parseFloat(form.ras_mel)) / parseFloat(form.ras_mel)) * 100).toFixed(1)}%</strong>
                </p>
              </div>
            )}
          </div>
        )}

        {/* ── STEP 3: STOCK ── */}
        {step === 'stock' && (
          <div className="space-y-4">
            <div>
              <label className="text-sm font-semibold text-gray-700 mb-1.5 block">Stock actuel *</label>
              <input
                type="number" inputMode="numeric" autoFocus
                value={form.stock}
                onChange={e => set('stock', e.target.value)}
                placeholder="0"
                className={`w-full px-4 py-3.5 rounded-2xl border bg-gray-50 outline-none focus:bg-white focus:border-indigo-500 ${errors.stock ? 'border-red-400' : 'border-gray-200'}`}
              />
              {errors.stock && <p className="text-red-500 text-sm mt-1">{errors.stock}</p>}
            </div>
            <div>
              <label className="text-sm font-semibold text-gray-700 mb-1.5 block">
                Alert quand stock {'<'} X
              </label>
              <input
                type="number" inputMode="numeric"
                value={form.stock_alert}
                onChange={e => set('stock_alert', e.target.value)}
                className="w-full px-4 py-3.5 rounded-2xl border border-gray-200 bg-gray-50 outline-none focus:bg-white focus:border-indigo-500"
              />
            </div>
            {/* Recap */}
            <div className="bg-indigo-50 border border-indigo-200 rounded-2xl p-4 space-y-1.5 text-sm">
              <h3 className="font-bold text-indigo-800 mb-2">Récap</h3>
              {[
                ['Référence', form.barcode],
                ['Article', form.name],
                ['Ras mel', form.ras_mel + ' DT'],
                ['Prix vente', form.prix_vente + ' DT'],
                ['Prix min', form.prix_min + ' DT'],
                ['Stock', form.stock + ' ' + form.unit],
              ].map(([l, v]) => (
                <div key={l} className="flex justify-between">
                  <span className="text-gray-500">{l}</span>
                  <span className="font-semibold font-mono">{v}</span>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* ── DONE ── */}
        {step === 'done' && (
          <div className="text-center py-12">
            <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <CheckCircle className="w-10 h-10 text-green-500" />
            </div>
            <h2 className="text-2xl font-bold text-gray-800 mb-1">Article ajouté! ✅</h2>
            <p className="text-gray-500 mb-1 font-semibold">{form.name}</p>
            <p className="text-gray-400 text-sm font-mono mb-8">{form.barcode}</p>
            <div className="flex flex-col gap-3">
              <button
                onClick={() => {
                  setForm({ name: '', barcode: '', category_id: '', unit: 'pièce', ras_mel: '', prix_vente: '', prix_min: '', stock: '', stock_alert: '5' })
                  setSuggestions([])
                  setStep('barcode')
                }}
                className="w-full py-4 bg-indigo-600 text-white rounded-2xl font-bold text-lg"
              >
                + Ajouter un autre
              </button>
              <Link href="/articles" className="w-full py-4 bg-gray-100 text-gray-700 rounded-2xl font-bold text-lg text-center block">
                Voir les articles
              </Link>
            </div>
          </div>
        )}

        {(step === 'info' || step === 'prix' || step === 'stock') && (
          <button
            onClick={next}
            disabled={loading}
            className="w-full mt-8 py-4 bg-indigo-600 text-white rounded-2xl font-bold text-lg active:scale-95 disabled:opacity-50 flex items-center justify-center gap-2"
          >
            {loading
              ? <span className="w-6 h-6 border-2 border-white border-t-transparent rounded-full animate-spin" />
              : step === 'stock' ? 'Enregistrer ✓' : 'Suivant →'}
          </button>
        )}
      </div>
    </div>
  )
}
